// Procedural powerup icons. Each (id × phase) renders to its own
// 40×40 canvas — same dimensions for every powerup at every phase so
// build-sheet and level-up cards can swap a phase 1 icon for phase 2
// without reflowing layout.
//
// Per-phase decoration stacks:
//   Phase 1 — base shape (rarity-colored frame + glyph).
//   Phase 2 — outer glow ring + 2 sparkles. Stronger frame.
//   Phase 3 — crown notch + halo + 4 sparkles. Final form.
//
// Result is cached so the same (id, phase) reuses one canvas.

import { POWERUPS, RARITY_COLOR, type Powerup, type PowerupPhase } from '../match/powerups';

export const POWERUP_ICON_SIZE = 40;

// Per-powerup glyph. Single unicode char drawn at the center of the
// frame. Picked to evoke the powerup's flavor; readable as a tiny icon.
const GLYPH: Record<string, string> = {
  energizer:    '⚡',
  gym_bro:      '💪',
  protein:      '🥤',
  late_night:   '🌙',
  caffeine:     '☕',
  meal_prep:    '🍱',
  finger_guns:  '👉',
  lucky_strike: '🍀',
  podcast:      '🎙',
  deep_breath:  '🫁',
  zen_garden:   '🪴',
  pre_workout:  '🔥',
  noise_cancel: '🎧',
  lean_build:   '🪶',
  big_energy:   '🔆',
  glass_cannon: '🔮',
  sidestepper:  '👟',
  pro_gamer:    '🎮',
  lifetime_sub: '💎',
  tumbler:      '🧃',
  sprinter:     '🏃',
  extended_ring:'⭕',
  mega_circle:  '🟠',
  headshot:     '🎯',
  main_char:    '🌟',
  protein_keg:  '🛢',
  phantom_step: '👤',
  viral_moment: '📈',
  transcendence:'👑',
};

const cache = new Map<string, string>();

export function makePowerupIcon(id: string, phase: PowerupPhase): string {
  const key = `${id}_${phase}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const p = POWERUPS.find((q) => q.id === id);
  if (!p) {
    const fallback = drawIcon('?', '#6b7280', phase);
    cache.set(key, fallback);
    return fallback;
  }
  const glyph = GLYPH[id] ?? '✦';
  const url = drawIcon(glyph, RARITY_COLOR[p.rarity], phase, p);
  cache.set(key, url);
  return url;
}

function drawIcon(
  glyph: string,
  color: string,
  phase: PowerupPhase,
  p?: Powerup,
): string {
  const S = POWERUP_ICON_SIZE;
  const c = document.createElement('canvas');
  c.width = S;
  c.height = S;
  const ctx = c.getContext('2d');
  if (!ctx) return '';

  // Phase 3: full halo behind everything.
  if (phase === 3) {
    const grad = ctx.createRadialGradient(S / 2, S / 2, 4, S / 2, S / 2, S / 2);
    grad.addColorStop(0, color + 'aa');
    grad.addColorStop(0.5, color + '55');
    grad.addColorStop(1, color + '00');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, S, S);
  }

  // Phase 2 & 3: outer glow ring.
  if (phase >= 2) {
    ctx.strokeStyle = color + 'cc';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(S / 2, S / 2, S / 2 - 2, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Inner frame — rarity color, thicker on higher phases.
  const frameInset = 4;
  const frameW = phase === 3 ? 2 : phase === 2 ? 1.6 : 1.2;
  ctx.strokeStyle = color;
  ctx.lineWidth = frameW;
  ctx.lineJoin = 'round';
  const r = 6;
  roundRect(ctx, frameInset, frameInset, S - frameInset * 2, S - frameInset * 2, r);
  ctx.stroke();

  // Plate — slight tint of the rarity color over a dark base.
  ctx.save();
  roundRect(ctx, frameInset + 0.5, frameInset + 0.5, S - frameInset * 2 - 1, S - frameInset * 2 - 1, r - 1);
  ctx.clip();
  ctx.fillStyle = '#0b0e16';
  ctx.fillRect(0, 0, S, S);
  ctx.fillStyle = color + '22';
  ctx.fillRect(0, 0, S, S);
  ctx.restore();

  // Body-mod / equipment categories get a tiny corner pip so the
  // category is readable at-a-glance even before reading the glyph.
  if (p) {
    const pip = p.category === 'body' ? '#f0abfc'
      : p.category === 'equipment' ? '#fde68a'
      : null;
    if (pip) {
      ctx.fillStyle = pip;
      ctx.beginPath();
      ctx.arc(S - 7, 7, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Faction tinted bar bottom for faction-locked powerups.
  if (p?.faction) {
    ctx.fillStyle = p.faction === 'ext' ? '#fb923c' : '#60a5fa';
    ctx.fillRect(frameInset + 2, S - frameInset - 3, S - frameInset * 2 - 4, 2);
  }

  // Center glyph.
  ctx.font = `${phase === 3 ? 22 : phase === 2 ? 20 : 18}px system-ui, "Apple Color Emoji", "Segoe UI Emoji"`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // shadow for readability over the tinted plate
  ctx.shadowColor = '#000';
  ctx.shadowBlur = 3;
  ctx.fillStyle = '#fff';
  ctx.fillText(glyph, S / 2, S / 2 + 1);
  ctx.shadowBlur = 0;

  // Phase 2: 2 sparkles.
  if (phase === 2) {
    drawSparkle(ctx, 8, 8, color, 1.4);
    drawSparkle(ctx, S - 8, S - 8, color, 1.4);
  }
  // Phase 3: crown notch + 4 sparkles.
  if (phase === 3) {
    // Crown — three triangles centered along the top.
    ctx.fillStyle = '#fde047';
    ctx.beginPath();
    ctx.moveTo(S / 2 - 6, 4);
    ctx.lineTo(S / 2 - 4, 0);
    ctx.lineTo(S / 2 - 2, 4);
    ctx.lineTo(S / 2,     0);
    ctx.lineTo(S / 2 + 2, 4);
    ctx.lineTo(S / 2 + 4, 0);
    ctx.lineTo(S / 2 + 6, 4);
    ctx.closePath();
    ctx.fill();

    drawSparkle(ctx, 6, S / 2, color, 1.6);
    drawSparkle(ctx, S - 6, S / 2, color, 1.6);
    drawSparkle(ctx, S / 2 - 10, S - 6, color, 1.2);
    drawSparkle(ctx, S / 2 + 10, S - 6, color, 1.2);
  }

  return c.toDataURL('image/png');
}

function drawSparkle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
  size: number,
): void {
  ctx.save();
  ctx.fillStyle = '#fff';
  ctx.shadowColor = color;
  ctx.shadowBlur = 6;
  ctx.beginPath();
  ctx.moveTo(x, y - size * 2);
  ctx.lineTo(x + size, y);
  ctx.lineTo(x, y + size * 2);
  ctx.lineTo(x - size, y);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
